/**
 * CR042 + CR043 + CR060: POC Run Detail Page - Text-First Pipeline Diagnostics
 * Shows READ (Self-Resolving) → SCAN → EXTRACT → VALIDATE step outputs
 * CR060: Dynamic tabs driven by prompt snapshot, shows parse_ok and skipped_reason
 * CR127: Unified run status utilities for consistency with Eye modal
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Loader2, Eye, Code2, FileJson, AlertTriangle, CheckCircle2, XCircle, Clock, Zap, FileText, Quote, BookOpen, SkipForward, AlertCircle, ExternalLink, ArrowRight, Stethoscope } from "lucide-react";
import { isUuidLike, safeHostname } from "@/lib/safeString";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ReadTabContent, ReadSummaryStats, SnapshotComparisonPanel } from "@/components/admin/poc-run";
import { ReadDiagnosticsDrawer } from "@/components/admin/poc-run/ReadDiagnosticsDrawer";
import { getExecutionPhases, getPhaseColor, type PhaseType } from "@/lib/poc-pipeline/phaseRegistry";
import { STATUS_COLORS, getEffectiveMerchantStatus, getEffectiveUnitStatus, isFailedStatus, isSucceededStatus } from "@/lib/poc-pipeline/runStatusUtils";
import type { PocRunMerchantRead, PocRunReadSummary, ReadStatus } from "@/lib/read-phase/types";
import { parseResolveNotes, getEngineLabel, getEngineBadgeVariant, type ParsedResolveNotes } from "@/lib/read-phase/resolveNotesParser";

interface PocRun {
  id: string;
  run_type: string;
  run_mode: string | null;
  status: string;
  merchant_limit_used: number;
  prompt_pack_id: string | null;
  prompt_snapshot_id: string | null;
  prompt_snapshot_hash: string | null;
  scan_model_used: string;
  validation_model_used: string | null;
  config_snapshot: any;
  totals_pages_scanned: number;
  totals_candidates: number;
  totals_deals_created: number;
  totals_deals_failed: number;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  created_at: string;
  // CR043: READ summary fields
  read_attempts_avg: number | null;
  merchants_readable: number | null;
  merchants_blocked: number | null;
  top_failure_reasons: Array<{ reason: string; count: number }> | null;
  // POC Run Units architecture fields
  origin_type: string | null;
  origin_id: string | null;
  parent_run_id: string | null;
  units_total: number;
  units_succeeded: number;
  units_failed: number;
  staged_count: number;
}

interface PocRunStep {
  id: string;
  poc_run_id: string;
  step_phase: string;
  prompt_key: string | null;
  prompt_snapshot_hash: string | null;
  merchant_id: string | null;
  source_id: string | null;
  page_number: number | null;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  created_at: string;
  // Text-first fields
  text_source_type: string | null;
  text_char_count: number | null;
  text_excerpt_used: string | null;
  input_candidate_text: string | null;
  input_context_text: string | null;
  // CR057: Source resolution fields
  resolve_source_url_used: string | null;
  resolve_content_type: string | null;
  resolve_extraction_quality_score: number | null;
  resolve_notes: string | null;
  // CR056: Tracking fields
  records_in: number | null;
  records_out: number | null;
  duration_ms: number | null;
  skipped_reason: string | null;
  step_order: number | null;
  // CR060: Parse tracking
  parse_ok: boolean | null;
  raw_input_sample: string | null;
  raw_output_sample: string | null;
  ai_model_used: string | null;
}

interface AiCallLog {
  id: string;
  poc_run_step_id: string;
  model_used: string;
  temperature: number | null;
  max_output_tokens: number | null;
  input_payload_json: any;
  input_image_refs: string[] | null;
  raw_response_text: string | null;
  parsed_response_json: any;
  parse_error: string | null;
  token_usage_json: any;
  duration_ms: number | null;
  created_at: string;
}

interface PocDeal {
  id: string;
  run_id: string;
  merchant_id: string;
  merchant_name: string | null;
  gift_card_brand: string;
  deal_type: string;
  buy_amount: number | null;
  reward_value: number | null;
  reward_unit: string;
  confidence: number;
  raw_ai_response: string | null;
  parsed_ai_json: any;
  ai_parse_error: string | null;
  scan_step_id: string | null;
  created_at: string;
  // Text-first fields
  candidate_text: string | null;
  context_text: string | null;
  deal_type_guess: string | null;
  evidence_where_found: string | null;
  validation_status: string | null;
  validation_reasons: any;
  validation_needs_visual: boolean | null;
  // CR058: Gate tracking fields
  final_status: string | null;
  blocked_reason_codes: string[] | null;
  blocked_reason_text: string | null;
  discount_percent: number | null;
  qualifying_spend_cad: number | null;
  // CR131: Benefit type classification fields (optional for backward compat)
  raw_benefit_text?: string | null;
  benefit_type?: string | null;
  validation_reason_code?: string | null;
}

interface PromptSnapshot {
  prompt_id: string;
  key: string;
  phase: string;
  prompt_text: string;
  enabled: boolean;
  order_index: number;
  model_override: string | null;
  temperature_override: number | null;
  max_tokens_override: number | null;
}

// CR127: Using shared STATUS_COLORS from runStatusUtils (imported above)

const STATUS_ICONS: Record<string, any> = {
  pending: Clock,
  running: Loader2,
  success: CheckCircle2,
  completed: CheckCircle2,
  succeeded: CheckCircle2,
  complete: CheckCircle2,
  failed: XCircle,
  failed_unknown: XCircle,
  canceled: AlertTriangle,
  skipped: Clock,
  timed_out: AlertCircle,
};

// CR127: Using shared getEffectiveMerchantStatus and getEffectiveUnitStatus from runStatusUtils

export default function PocRunDetail() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [run, setRun] = useState<PocRun | null>(null);
  const [steps, setSteps] = useState<PocRunStep[]>([]);
  const [aiLogs, setAiLogs] = useState<AiCallLog[]>([]);
  const [deals, setDeals] = useState<PocDeal[]>([]);
  const [promptSnapshot, setPromptSnapshot] = useState<PromptSnapshot[]>([]);
  const [merchants, setMerchants] = useState<PocRunMerchantRead[]>([]);
  const [units, setUnits] = useState<PocRunUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  
  // Dialog states
  const [selectedStep, setSelectedStep] = useState<PocRunStep | null>(null);
  const [selectedAiLog, setSelectedAiLog] = useState<AiCallLog | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<PocDeal | null>(null);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [showAiLogDialog, setShowAiLogDialog] = useState(false);
  const [showDealDialog, setShowDealDialog] = useState(false);
  // CR385: Diagnostics drawer state
  const [diagStep, setDiagStep] = useState<PocRunStep | null>(null);
  const [diagParsed, setDiagParsed] = useState<ParsedResolveNotes | null>(null);
  const [showDiagDrawer, setShowDiagDrawer] = useState(false);

  // POC Run Unit interface for units table
  interface PocRunUnit {
    id: string;
    poc_run_id: string;
    batch_run_id: string | null;
    step_key: string;
    merchant_source_id: string | null;
    merchant_key: string | null;
    status: "NOT_STARTED" | "RUNNING" | "COMPLETE" | "FAILED" | "TIMED_OUT";
    started_at: string | null;
    finished_at: string | null;
    duration_ms: number | null;
    attempt_no: number;
    records_out: number;
    error_message: string | null;
    artifacts_json: any;
    stats_json: {
      candidates_found?: number;
      validated_count?: number;
      staged_count?: number;
    } | null;
    created_at: string;
    // CR600: Enriched fields for WEB source URL resolution
    _effective_url?: string | null;
    _scan_type?: string | null;
    _source_invalid?: boolean;
  }

  const fetchRunData = async () => {
    if (!runId) return;
    
    try {
      // Fetch run
      const { data: runData, error: runError } = await supabase
        .from("poc_runs")
        .select("*")
        .eq("id", runId)
        .single();
      
      if (runError) throw runError;
      // Cast top_failure_reasons from Json to proper type
      const typedRun = {
        ...runData,
        top_failure_reasons: (runData.top_failure_reasons as unknown as Array<{ reason: string; count: number }>) || [],
      } as PocRun;
      setRun(typedRun);
      
      // Fetch prompt snapshot
      if (runData.prompt_snapshot_id) {
        const { data: snapshotData } = await supabase
          .from("prompt_pack_snapshots")
          .select("snapshot_json")
          .eq("id", runData.prompt_snapshot_id)
          .single();
        
        if (snapshotData?.snapshot_json) {
          setPromptSnapshot(snapshotData.snapshot_json as unknown as PromptSnapshot[]);
        }
      }
      
      // Fetch steps
      const { data: stepsData } = await supabase
        .from("poc_run_steps")
        .select("*")
        .eq("poc_run_id", runId)
        .order("created_at");
      
      setSteps((stepsData as PocRunStep[]) || []);
      
      // Fetch AI logs for all steps
      if (stepsData && stepsData.length > 0) {
        const stepIds = stepsData.map(s => s.id);
        const { data: logsData } = await supabase
          .from("ai_call_logs")
          .select("*")
          .in("poc_run_step_id", stepIds)
          .order("created_at");
        
        setAiLogs((logsData as unknown as AiCallLog[]) || []);
      }
      
      // Fetch deals
      const { data: dealsData } = await supabase
        .from("poc_deals")
        .select("*")
        .eq("run_id", runId)
        .order("created_at");
      
      setDeals((dealsData as PocDeal[]) || []);

      // CR043: Fetch merchants with READ data
      const { data: merchantsData } = await supabase
        .from("poc_run_merchants")
        .select("*")
        .eq("poc_run_id", runId)
        .order("merchant_name");

      // Fetch poc_run_units if this is a BATCH_S2 run
      let unitsData: any[] | null = null;
      if (runData.run_mode === "BATCH_S2" || runData.origin_type === "BATCH_RUN") {
        const { data } = await supabase
          .from("poc_run_units")
          .select("*")
          .eq("poc_run_id", runId)
          .order("created_at");
        unitsData = data;
      }

      // CR600: Resolve correct WEB source URLs from merchant_sources
      // Use config_snapshot.merchant_source_ids or unit merchant_source_ids as source of truth
      const configSnapshot = runData.config_snapshot as Record<string, unknown> | null;
      const sourceIdsFromConfig = (configSnapshot?.merchant_source_ids as string[]) || [];
      const sourceIdsFromUnits = (unitsData || [])
        .map((u: any) => u.merchant_source_id)
        .filter(Boolean);
      // Validate/normalize: only keep valid UUID strings to prevent query errors
      const allSourceIds = [...new Set([...sourceIdsFromConfig, ...sourceIdsFromUnits])]
        .map(String)
        .filter(isUuidLike);

      // Build a map: String(merchant_source_id) → { effective_url, scan_type, merchant_id }
      const sourceUrlMap = new Map<string, { effective_url: string; scan_type: string | null; merchant_id: string }>();
      if (allSourceIds.length > 0) {
        const { data: sourcesData } = await supabase
          .from("merchant_sources")
          .select("id, merchant_id, source_url, normalized_url, scan_type")
          .in("id", allSourceIds);
        
        (sourcesData || []).forEach((s: any) => {
          sourceUrlMap.set(String(s.id), {
            effective_url: s.normalized_url || s.source_url,
            scan_type: s.scan_type || null,  // Don't default to 'WEB'; treat missing as unknown
            merchant_id: s.merchant_id,
          });
        });
      }

      // CR600: Resolve merchant _effective_url via unit→source-id linkage (deterministic)
      // Map by merchant_key (preferred) from unit.merchant_source_id join
      const merchantSourceMap = new Map<string, { effective_url: string; scan_type: string | null }>();
      (unitsData || []).forEach((u: any) => {
        if (u.merchant_source_id) {
          const sourceInfo = sourceUrlMap.get(String(u.merchant_source_id));
          if (sourceInfo) {
            // Use merchant_key from the unit as the canonical lookup key
            const key = u.merchant_key || sourceInfo.merchant_id;
            if (key && !merchantSourceMap.has(key)) {
              merchantSourceMap.set(key, {
                effective_url: sourceInfo.effective_url,
                scan_type: sourceInfo.scan_type,
              });
            }
          }
        }
      });
      // Fallback for runs without units (e.g., non-S2 runs): use config source IDs
      // Same key logic: use merchant_id from merchant_sources as the key
      if (!unitsData || unitsData.length === 0) {
        sourceUrlMap.forEach((info) => {
          const key = info.merchant_id;
          if (key && !merchantSourceMap.has(key)) {
            merchantSourceMap.set(key, {
              effective_url: info.effective_url,
              scan_type: info.scan_type,
            });
          }
        });
      }

      // Enrich merchants with _effective_url (keep raw source_url intact for debugging)
      // Lookup by merchant_id first (matches merchant_key in most cases), fallback to merchant_name normalization
      const enrichedMerchants = (merchantsData || []).map((m: any) => {
        const resolved = merchantSourceMap.get(m.merchant_id) || null;
        return {
          ...m,
          _effective_url: resolved?.effective_url || null,
          _scan_type: resolved?.scan_type || null,
          _source_invalid: resolved ? resolved.scan_type !== 'WEB' : false,
        };
      });
      setMerchants(enrichedMerchants as unknown as PocRunMerchantRead[]);

      // CR600: Enrich units with effective_url from merchant_sources
      const enrichedUnits = (unitsData || []).map((u: any) => {
        const sourceInfo = u.merchant_source_id ? sourceUrlMap.get(String(u.merchant_source_id)) : null;
        return {
          ...u,
          _effective_url: sourceInfo?.effective_url || null,
          _scan_type: sourceInfo?.scan_type || null,
          _source_invalid: sourceInfo ? sourceInfo.scan_type !== 'WEB' : false,
        };
      });
      setUnits(enrichedUnits as unknown as PocRunUnit[]);
      
    } catch (err) {
      console.error("Error fetching run data:", err);
      toast.error("Failed to load run details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && runId) {
      fetchRunData();
    }
  }, [isAdmin, runId]);

  const handleDelete = async () => {
    if (!runId) return;
    
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Please sign in");
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/poc-scan-run`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: "delete", run_id: runId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to delete run");
      }

      toast.success("Run deleted successfully");
      navigate("/admin/poc-runs");
    } catch (err: any) {
      console.error("Error deleting run:", err);
      toast.error(err.message || "Failed to delete run");
    } finally {
      setDeleting(false);
    }
  };

  // CR060: Use phase registry for execution phases
  const EXECUTION_PHASES = getExecutionPhases();
  
  const orderedPhases = useMemo(() => {
    const phases: string[] = [];
    
    if (promptSnapshot.length > 0) {
      // Add enabled phases from the snapshot in order_index order
      // Filter to only include execution phases (exclude SYSTEM, SCHEMA, etc.)
      const enabledPrompts = [...promptSnapshot]
        .filter(p => p.enabled && EXECUTION_PHASES.includes(p.phase as PhaseType))
        .sort((a, b) => a.order_index - b.order_index);
      
      // Extract unique phases in order of first occurrence
      enabledPrompts.forEach(p => {
        if (!phases.includes(p.phase)) {
          phases.push(p.phase);
        }
      });
    }
    
    // Also include any phases that have actual steps recorded (for legacy runs or phases not in snapshot)
    // Only include execution phases
    const stepsPhases = new Set(steps.map(s => s.step_phase).filter(p => EXECUTION_PHASES.includes(p as PhaseType)));
    stepsPhases.forEach(phase => {
      if (!phases.includes(phase)) {
        phases.push(phase);
      }
    });
    
    // If we have no phases at all (no snapshot and no steps), show minimal fallback
    if (phases.length === 0) {
      return ["READ", "SCAN", "EXTRACT", "VALIDATE"];
    }
    
    return phases;
  }, [promptSnapshot, steps, EXECUTION_PHASES]);

  // CR060: Compute phase statistics for warnings
  const phaseStats = useMemo(() => {
    const stats: Record<string, { total: number; skipped: number; parseFailed: number; recordsOut: number }> = {};
    
    steps.forEach(step => {
      if (!stats[step.step_phase]) {
        stats[step.step_phase] = { total: 0, skipped: 0, parseFailed: 0, recordsOut: 0 };
      }
      stats[step.step_phase].total++;
      if (step.status === 'skipped') stats[step.step_phase].skipped++;
      if (step.parse_ok === false) stats[step.step_phase].parseFailed++;
      stats[step.step_phase].recordsOut += step.records_out || 0;
    });
    
    return stats;
  }, [steps]);

  const stepsByPhase = useMemo(() => {
    // Initialize with all ordered phases (ensures empty phases still appear)
    const grouped: Record<string, PocRunStep[]> = {};
    orderedPhases.forEach(phase => {
      grouped[phase] = [];
    });
    
    steps.forEach(step => {
      if (grouped[step.step_phase]) {
        grouped[step.step_phase].push(step);
      } else {
        grouped[step.step_phase] = [step];
      }
    });
    
    return grouped;
  }, [steps, orderedPhases]);

  // CR057: Compute step status summary for header
  const stepStatusSummary = useMemo(() => {
    let failed = 0;
    const warn = 0;
    let success = 0;
    let skipped = 0;
    
    steps.forEach(step => {
      if (step.status === 'failed') failed++;
      else if (step.status === 'skipped') skipped++;
      else if (step.status === 'success' || step.status === 'completed') success++;
    });
    
    return { failed, warn, success, skipped, total: steps.length };
  }, [steps]);

  // CR043: Compute READ summary from run data
  const readSummary: PocRunReadSummary = useMemo(() => ({
    read_attempts_avg: run?.read_attempts_avg || null,
    merchants_readable: run?.merchants_readable || 0,
    merchants_blocked: run?.merchants_blocked || 0,
    top_failure_reasons: run?.top_failure_reasons || [],
  }), [run]);

  // CR043: Check if any merchant has unreadable status (for SCAN skip display)
  const merchantsWithReadFailure = useMemo(() => {
    return new Set(
      merchants
        .filter(m => m.read_status === 'UNREADABLE_SOURCE' || m.read_status === 'SKIPPED')
        .map(m => m.merchant_id)
    );
  }, [merchants]);

  // Computed aggregation from units/merchants when available (fixes parent run summary showing 0)
  const aggregatedStats = useMemo(() => {
    // If we have units (BATCH_S2 run), compute from units
    if (units.length > 0) {
      // CR127: Use isSucceededStatus/isFailedStatus helpers for consistent status checking
      const succeeded = units.filter(u => isSucceededStatus(getEffectiveUnitStatus(u))).length;
      const failed = units.filter(u => isFailedStatus(getEffectiveUnitStatus(u))).length;
      const totalDeals = units.reduce((sum, u) => sum + (u.stats_json?.validated_count || u.records_out || 0), 0);
      const totalCandidates = units.reduce((sum, u) => sum + (u.stats_json?.candidates_found || 0), 0);
      
      return {
        merchantsTotal: units.length,
        merchantsSucceeded: succeeded,
        merchantsFailed: failed,
        pagesScanned: 0, // Not tracked at unit level
        candidates: totalCandidates || run?.totals_candidates || 0,
        dealsCreated: totalDeals || run?.totals_deals_created || 0,
        dealsFailed: run?.totals_deals_failed || 0,
        hasPartialFailure: failed > 0 && succeeded > 0,
      };
    }
    
    // Otherwise use run-level totals
    return {
      merchantsTotal: run?.merchant_limit_used || 0,
      merchantsSucceeded: run?.units_succeeded || 0,
      merchantsFailed: run?.units_failed || 0,
      pagesScanned: run?.totals_pages_scanned || 0,
      candidates: run?.totals_candidates || 0,
      dealsCreated: run?.totals_deals_created || 0,
      dealsFailed: run?.totals_deals_failed || 0,
      hasPartialFailure: (run?.units_failed || 0) > 0 && (run?.units_succeeded || 0) > 0,
    };
  }, [units, run]);

  const getAiLogForStep = (stepId: string) => {
    return aiLogs.find(log => log.poc_run_step_id === stepId);
  };

  const formatDuration = (start: string | null, end: string | null) => {
    if (!start) return "-";
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const seconds = Math.round((endDate.getTime() - startDate.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!run) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-muted-foreground">
          Run not found
        </div>
      </AdminLayout>
    );
  }

  const StatusIcon = STATUS_ICONS[run.status] || Clock;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/poc-runs")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                POC Run Detail
                <Badge className={STATUS_COLORS[run.status]}>
                  <StatusIcon className={`h-3 w-3 mr-1 ${run.status === "running" ? "animate-spin" : ""}`} />
                  {run.status}
                </Badge>
                {run.run_type?.includes("v2") && (
                  <Badge variant="outline" className="text-blue-600 border-blue-600">
                    Text-First v2
                  </Badge>
                )}
                {/* CR057: Step status summary badges */}
                {stepStatusSummary.failed > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {stepStatusSummary.failed} step{stepStatusSummary.failed > 1 ? 's' : ''} failed
                  </Badge>
                )}
                {stepStatusSummary.skipped > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {stepStatusSummary.skipped} skipped
                  </Badge>
                )}
              </h1>
              <p className="text-muted-foreground text-sm">
                {run.id}
              </p>
            </div>
          </div>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={deleting}>
                {deleting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Delete Run
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this POC run?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all artifacts: {deals.length} deals, {steps.length} steps, 
                  and all AI call logs. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Origin Banner for Batch Run */}
        {run.origin_type === "BATCH_RUN" && (
          <Card className="border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-600">Batch Run Origin</Badge>
                  <span className="text-sm text-muted-foreground">
                    This run was triggered from a Batch Run S2 step
                  </span>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <a href={`/admin/batch-runs/${run.origin_id}`}>View Batch Run</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Units Panel for BATCH_S2 runs */}
        {(run.run_mode === "BATCH_S2" || run.origin_type === "BATCH_RUN") && units.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Processing Units ({units.length})</CardTitle>
              <CardDescription>Per-merchant execution status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{run.units_total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{run.units_succeeded}</p>
                  <p className="text-xs text-muted-foreground">Succeeded</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{run.units_failed}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{run.staged_count}</p>
                  <p className="text-xs text-muted-foreground">Staged</p>
                </div>
              </div>
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Merchant</TableHead>
                      <TableHead>Source URL</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead className="text-right">Staged</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {units.map(unit => {
                      const effectiveStatus = getEffectiveUnitStatus(unit);
                      return (
                        <TableRow key={unit.id}>
                          <TableCell className="font-medium">{unit.merchant_key || unit.merchant_source_id}</TableCell>
                          <TableCell className="max-w-[200px]">
                            {unit._source_invalid ? (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {unit._scan_type 
                                  ? `Invalid source type (${unit._scan_type})` 
                                  : "Missing/Unknown source type"}
                              </Badge>
                            ) : unit._effective_url ? (
                              <a
                                href={unit._effective_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1 truncate"
                              >
                                {safeHostname(unit._effective_url)}
                                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                              </a>
                            ) : (
                              <span className="text-muted-foreground text-xs">Missing WEB source URL</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[effectiveStatus] || "bg-gray-500"}>
                              {effectiveStatus.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {unit.duration_ms ? `${Math.round(unit.duration_ms / 1000)}s` : "-"}
                          </TableCell>
                          <TableCell className="text-right">{unit.records_out}</TableCell>
                          <TableCell className="text-xs text-red-600 max-w-[200px] truncate">
                            {unit.error_message || "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Partial Failure Banner */}
        {aggregatedStats.hasPartialFailure && (
          <Card className="border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20">
            <CardContent className="py-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                <span className="font-medium text-yellow-800 dark:text-yellow-200">
                  Website Collect completed with errors
                </span>
                <span className="text-sm text-yellow-700 dark:text-yellow-300">
                  ({aggregatedStats.merchantsFailed} of {aggregatedStats.merchantsTotal} merchants failed)
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Run Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Run Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Merchants</p>
                <p className="text-2xl font-bold">
                  {aggregatedStats.merchantsSucceeded}/{aggregatedStats.merchantsTotal}
                  {aggregatedStats.merchantsFailed > 0 && (
                    <span className="text-red-500 text-sm ml-1">
                      ({aggregatedStats.merchantsFailed} failed)
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pages Scanned</p>
                <p className="text-2xl font-bold">{aggregatedStats.pagesScanned}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Candidates</p>
                <p className="text-2xl font-bold">{aggregatedStats.candidates}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Deals Created</p>
                <p className="text-2xl font-bold text-green-600">{aggregatedStats.dealsCreated}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold text-red-600">{aggregatedStats.dealsFailed}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="text-2xl font-bold">{formatDuration(run.started_at, run.finished_at)}</p>
              </div>
            </div>
            
            {/* CR043: READ Summary Stats */}
            {(readSummary.merchants_readable > 0 || readSummary.merchants_blocked > 0) && (
              <div className="mt-6 pt-4 border-t">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  READ Phase Summary
                </h4>
                <ReadSummaryStats summary={readSummary} totalMerchants={run.merchant_limit_used} />
              </div>
            )}
            
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Run Type:</span>
                  <span className="font-mono">{run.run_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scan Model:</span>
                  <span className="font-mono">{run.scan_model_used}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Validation Model:</span>
                  <span className="font-mono">{run.validation_model_used || "-"}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Snapshot ID:</span>
                  <span className="font-mono text-xs">{run.prompt_snapshot_id?.substring(0, 8) || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Snapshot Hash:</span>
                  <span className="font-mono text-xs">{run.prompt_snapshot_hash?.substring(0, 12) || "-"}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pipeline Step Outputs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Pipeline Step Outputs
            </CardTitle>
            <CardDescription>
              {/* Dynamic execution order from prompt snapshot */}
              {promptSnapshot.length > 0 ? (
                promptSnapshot
                  .filter(p => p.enabled)
                  .sort((a, b) => a.order_index - b.order_index)
                  .map(p => p.phase)
                  .join(' → ')
              ) : (
                orderedPhases.join(' → ')
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* CR057: Dynamic tabs driven by prompt snapshot */}
            <Tabs defaultValue={orderedPhases[0] || "READ"}>
              <TabsList className="flex-wrap h-auto">
                {orderedPhases.map((phase) => {
                  const phaseSteps = stepsByPhase[phase] || [];
                  // CR385: Badge count = rendered row count for the phase
                  // READ tab shows merchant count, other phases show step count (matches rendered rows)
                  const recordsOutSum = phaseSteps.reduce((sum, s) => sum + (s.records_out || 0), 0);
                  const outputCount = phase === 'READ' 
                    ? merchants.length 
                    : (recordsOutSum > 0 ? recordsOutSum : phaseSteps.length);
                  const stepCount = phaseSteps.length;
                  const hasFailures = phaseSteps.some(s => s.status === 'failed');
                  const hasSkipped = phaseSteps.some(s => s.status === 'skipped');
                  const hasParseFailed = phaseSteps.some(s => s.parse_ok === false);
                  
                  return (
                    <TabsTrigger key={phase} value={phase} className="relative">
                      {phase === 'READ' && <BookOpen className="h-3 w-3 mr-1" />}
                      {phase}
                      <Badge 
                        variant={hasFailures || hasParseFailed ? "destructive" : hasSkipped ? "secondary" : "secondary"} 
                        className="ml-1.5 h-5 px-1.5 text-xs"
                        title={phase !== 'READ' ? `${stepCount} step${stepCount !== 1 ? 's' : ''}, ${outputCount} outputs` : `${outputCount} merchants`}
                      >
                        {outputCount}
                      </Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              
              {/* CR043: READ Tab with Self-Resolving Content */}
              <TabsContent value="READ">
                <ReadTabContent merchants={merchants} />
              </TabsContent>
              
              {/* CR060: Dynamic tab content for all phases except READ */}
              {orderedPhases.filter(phase => phase !== 'READ').map((phase) => {
                const phaseSteps = stepsByPhase[phase] || [];
                const stats = phaseStats[phase] || { total: 0, skipped: 0, parseFailed: 0, recordsOut: 0 };
                
                // Get step-specific column config
                const getStepColumns = (phase: string) => {
                  switch (phase) {
                    case 'SCAN':
                      return { showTextSource: true, showCharCount: true, showCandidateText: false, showSourceUrl: true, showRecords: true };
                    case 'EXTRACT':
                    case 'VALIDATE':
                      return { showTextSource: false, showCharCount: false, showCandidateText: true, showSourceUrl: false, showRecords: true };
                    case 'READ_DETAILS':
                      return { showTextSource: true, showCharCount: true, showCandidateText: false, showSourceUrl: true, showRecords: true };
                    case 'SCAN_DETAILS':
                    case 'MERGE':
                      return { showTextSource: false, showCharCount: false, showCandidateText: true, showSourceUrl: false, showRecords: true };
                    case 'NORMALIZE':
                    case 'DEDUPE':
                      return { showTextSource: false, showCharCount: false, showCandidateText: true, showSourceUrl: false, showRecords: true };
                    default:
                      return { showTextSource: false, showCharCount: false, showCandidateText: false, showSourceUrl: false, showRecords: true };
                  }
                };
                
                const columns = getStepColumns(phase);
                
                return (
                  <TabsContent key={phase} value={phase}>
                    {/* CR060: Phase warnings */}
                    {stats.parseFailed > 0 && (
                      <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-800 dark:text-red-200">
                          <strong>{stats.parseFailed}</strong> step{stats.parseFailed > 1 ? 's' : ''} had JSON parse failures. Check raw output for debugging.
                        </div>
                      </div>
                    )}
                    
                    {phaseSteps.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <SkipForward className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No {phase} steps recorded</p>
                        <p className="text-xs mt-1">This phase was not executed or is not enabled in the pack</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[400px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Status</TableHead>
                              <TableHead>Merchant</TableHead>
                              {columns.showRecords && <TableHead>In/Out</TableHead>}
                              {columns.showTextSource && <TableHead>Engine / Source</TableHead>}
                              {columns.showCharCount && <TableHead>Chars</TableHead>}
                              {columns.showCandidateText && <TableHead>Candidate</TableHead>}
                              {columns.showSourceUrl && <TableHead>Source</TableHead>}
                              <TableHead>Duration</TableHead>
                              <TableHead>Details</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {phaseSteps.map(step => {
                              const aiLog = getAiLogForStep(step.id);
                              const StepIcon = STATUS_ICONS[step.status] || Clock;
                              const isSkipped = step.status === 'skipped';
                              const isParseFailed = step.parse_ok === false;
                              
                              return (
                                <TableRow key={step.id} className={isParseFailed ? "bg-red-50 dark:bg-red-950/20" : isSkipped ? "bg-gray-50 dark:bg-gray-900/20" : ""}>
                                  <TableCell>
                                    <div className="flex flex-col gap-1">
                                      <Badge className={STATUS_COLORS[step.status]}>
                                        <StepIcon className={`h-3 w-3 mr-1 ${step.status === "running" ? "animate-spin" : ""}`} />
                                        {step.status}
                                      </Badge>
                                      {isParseFailed && (
                                        <Badge variant="destructive" className="text-xs">
                                          Parse Failed
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {step.merchant_id || "-"}
                                  </TableCell>
                                  {columns.showRecords && (
                                    <TableCell className="text-xs">
                                      <div className="flex flex-col">
                                        <span className="text-muted-foreground">in: {step.records_in ?? '-'}</span>
                                        <span className="font-medium">out: {step.records_out ?? '-'}</span>
                                        {/* CR386: Enhanced SCAN "Why 0" inline diagnostic */}
                                        {phase === 'SCAN' && (() => {
                                          const scanParsed = parseResolveNotes(step.resolve_notes);
                                          const sd = scanParsed.scan_diag;
                                          if (sd) {
                                            // CR386: Show links_in → kept → candidates flow with gate reason
                                            const keptCount = sd.links_kept_count ?? sd.links_in_count;
                                            const isSeeded = sd.gate_reason === "LINKS_SEEDED";
                                            const isFallback = sd.gate_reason === "FALLBACK_CANONICAL" || sd.gate_reason === "FALLBACK_CONFIGURED_URL";
                                            const isEmitted = sd.gate_reason === "EMITTED";
                                            const colorClass = isEmitted ? 'text-green-600 dark:text-green-400'
                                              : isSeeded ? 'text-purple-600 dark:text-purple-400'
                                              : isFallback ? 'text-blue-600 dark:text-blue-400'
                                              : 'text-amber-600 dark:text-amber-400';
                                            return (
                                              <div className="flex flex-col gap-0.5 mt-0.5">
                                                <span className={`text-[10px] ${colorClass}`}
                                                  title={`links_in=${sd.links_in_count} → kept=${keptCount} → candidates=${sd.candidates_emitted_count}`}>
                                                  {sd.gate_reason} ({sd.links_in_count} → {keptCount} → {sd.candidates_emitted_count})
                                                </span>
                                                {sd.fallback_url_used && (
                                                  <span className="text-[10px] text-blue-600 dark:text-blue-400" title={sd.fallback_url_used}>
                                                    Fallback: {sd.fallback_url_used.length > 40 ? sd.fallback_url_used.substring(0, 40) + '…' : sd.fallback_url_used}
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          }
                                          // Fallback: show notes from resolve_notes if present
                                          if (scanParsed.notes) {
                                            return <span className="text-[10px] text-muted-foreground mt-0.5">{scanParsed.notes}</span>;
                                          }
                                          return null;
                                        })()}
                                      </div>
                                    </TableCell>
                                  )}
                                  {columns.showTextSource && (() => {
                                    const parsed = parseResolveNotes(step.resolve_notes);
                                    const hasRedirect = parsed.url_lineage.final_url
                                      && parsed.url_lineage.requested_url
                                      && parsed.url_lineage.final_url !== parsed.url_lineage.requested_url;
                                    return (
                                      <TableCell>
                                        <div className="flex flex-col gap-1">
                                          <div className="flex items-center gap-1.5">
                                            <Badge
                                              variant={getEngineBadgeVariant(parsed.acquisition_engine)}
                                              className="cursor-pointer text-[10px] px-1.5 py-0"
                                              onClick={() => {
                                                setDiagStep(step);
                                                setDiagParsed(parsed);
                                                setShowDiagDrawer(true);
                                              }}
                                            >
                                              {getEngineLabel(parsed.acquisition_engine)}
                                            </Badge>
                                            {hasRedirect && (
                                              <TooltipProvider>
                                                <Tooltip>
                                                  <TooltipTrigger>
                                                    <ArrowRight className="h-3 w-3 text-amber-500" />
                                                  </TooltipTrigger>
                                                  <TooltipContent className="max-w-xs text-xs">
                                                    Redirect: {parsed.url_lineage.final_url}
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                            )}
                                          </div>
                                          {parsed.acquisition_engine !== "firecrawl_v2" && (
                                            <span className="text-[10px] text-muted-foreground leading-tight">
                                              {parsed.engine_reason || (step.step_phase === 'SCAN' ? 'Derived from READ output' : `Firecrawl not invoked (engine=${parsed.acquisition_engine})`)}
                                            </span>
                                          )}
                                        </div>
                                      </TableCell>
                                    );
                                  })()}
                                  {columns.showCharCount && (
                                    <TableCell className="text-xs">
                                      {step.text_char_count ? step.text_char_count.toLocaleString() : "-"}
                                    </TableCell>
                                  )}
                                  {columns.showCandidateText && (
                                    <TableCell className="max-w-[150px] truncate" title={step.input_candidate_text || ""}>
                                      {step.input_candidate_text ? (
                                        <span className="text-xs italic">"{step.input_candidate_text.substring(0, 40)}..."</span>
                                      ) : "-"}
                                    </TableCell>
                                  )}
                                  {columns.showSourceUrl && (
                                    <TableCell className="max-w-[100px] truncate" title={step.resolve_source_url_used || ""}>
                                      {step.resolve_source_url_used ? (
                                        <a 
                                          href={step.resolve_source_url_used} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-xs text-blue-600 hover:underline"
                                        >
                                          {(() => {
                                            try {
                                              return new URL(step.resolve_source_url_used).hostname;
                                            } catch {
                                              return step.resolve_source_url_used.substring(0, 20);
                                            }
                                          })()}
                                        </a>
                                      ) : "-"}
                                    </TableCell>
                                  )}
                                  <TableCell className="text-xs">
                                    {step.duration_ms ? `${step.duration_ms}ms` : formatDuration(step.started_at, step.finished_at)}
                                  </TableCell>
                                  <TableCell className="max-w-[150px]">
                                    {step.skipped_reason ? (
                                      <Badge variant="secondary" className="text-xs">
                                        <SkipForward className="h-3 w-3 mr-1" />
                                        {step.skipped_reason}
                                      </Badge>
                                    ) : step.error_message ? (
                                      <span className="text-xs text-red-600 truncate block" title={step.error_message}>
                                        {step.error_message.substring(0, 30)}...
                                      </span>
                                    ) : step.ai_model_used ? (
                                      <span className="text-xs text-muted-foreground">{step.ai_model_used.replace('gemini-', '')}</span>
                                    ) : "-"}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex justify-end gap-1">
                                      {/* CR386: Diagnostics button for READ + SCAN phases */}
                                      {(columns.showTextSource || phase === 'SCAN') && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => {
                                                  setDiagStep(step);
                                                  setDiagParsed(parseResolveNotes(step.resolve_notes));
                                                  setShowDiagDrawer(true);
                                                }}
                                              >
                                                <Stethoscope className="h-4 w-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>View Diagnostics</TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() => {
                                                setSelectedStep(step);
                                                setShowStepDialog(true);
                                              }}
                                            >
                                              <Eye className="h-4 w-4" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>View Step Details</TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                      {aiLog && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => {
                                                  setSelectedAiLog(aiLog);
                                                  setShowAiLogDialog(true);
                                                }}
                                              >
                                                <Code2 className="h-4 w-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>View Raw Input/Output</TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          </CardContent>
        </Card>

        {/* Prompt Snapshot */}
        {promptSnapshot.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileJson className="h-5 w-5" />
                Prompt Snapshot (Frozen at Run Start)
              </CardTitle>
              <CardDescription>
                These exact prompts were used for this run
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-4">
                  {promptSnapshot.map((p, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={p.enabled ? "default" : "secondary"}>
                            {p.phase}
                          </Badge>
                          <span className="font-mono text-sm">{p.key}</span>
                        </div>
                        {p.model_override && (
                          <span className="text-xs text-muted-foreground">
                            Model: {p.model_override}
                          </span>
                        )}
                      </div>
                      <pre className="text-xs bg-muted p-3 rounded overflow-x-auto max-h-[150px]">
                        {p.prompt_text}
                      </pre>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* CR054: Snapshot Comparison Panel */}
        <SnapshotComparisonPanel
          promptPackId={run.prompt_pack_id}
          promptSnapshot={promptSnapshot}
          snapshotHash={run.prompt_snapshot_hash}
          snapshotCreatedAt={run.started_at}
        />

        {/* Deals with Text-First Data + CR058: Created/Blocked/Failed tabs */}
        <Card>
          <CardHeader>
            <CardTitle>Deal Outcomes ({deals.length} total)</CardTitle>
            <CardDescription>
              Click to see candidate text, extracted fields, and validation results
            </CardDescription>
          </CardHeader>
          <CardContent>
            {deals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No deals processed in this run
              </div>
            ) : (
              <Tabs defaultValue="created" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="created" className="gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Created ({deals.filter(d => d.final_status === "created" || !d.final_status).length})
                  </TabsTrigger>
                  <TabsTrigger value="blocked" className="gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    Blocked ({deals.filter(d => d.final_status === "blocked").length})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="created">
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Merchant</TableHead>
                          <TableHead>Brand</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Value</TableHead>
                          <TableHead>Candidate Text</TableHead>
                          <TableHead>Validation</TableHead>
                          <TableHead>Confidence</TableHead>
                          <TableHead className="text-right">Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deals.filter(d => d.final_status === "created" || !d.final_status).map(deal => (
                          <TableRow key={deal.id}>
                            <TableCell className="font-medium">
                              {deal.merchant_name || deal.merchant_id}
                            </TableCell>
                            <TableCell>{deal.gift_card_brand || "-"}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{deal.deal_type}</Badge>
                            </TableCell>
                            <TableCell>
                              {deal.reward_value ? `${deal.reward_value} ${deal.reward_unit}` : 
                               deal.discount_percent ? `${deal.discount_percent}% off` : "-"}
                            </TableCell>
                            <TableCell className="max-w-[150px] truncate" title={deal.candidate_text || ""}>
                              {deal.candidate_text ? (
                                <span className="text-xs italic">"{deal.candidate_text.substring(0, 40)}..."</span>
                              ) : "-"}
                            </TableCell>
                            <TableCell>
                              {deal.validation_status ? (
                                <Badge variant={deal.validation_status === "valid" ? "default" : "destructive"}>
                                  {deal.validation_status}
                                </Badge>
                              ) : "-"}
                            </TableCell>
                            <TableCell>
                              <Badge variant={deal.confidence >= 0.8 ? "default" : deal.confidence >= 0.6 ? "secondary" : "destructive"}>
                                {Math.round(deal.confidence * 100)}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedDeal(deal);
                                  setShowDealDialog(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </TabsContent>
                
                <TabsContent value="blocked">
                  {deals.filter(d => d.final_status === "blocked").length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No blocked deals - all valid candidates passed the gate
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Merchant</TableHead>
                            <TableHead>Brand</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Blocked Reason</TableHead>
                            <TableHead>Values Found</TableHead>
                            <TableHead>Confidence</TableHead>
                            <TableHead className="text-right">Details</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deals.filter(d => d.final_status === "blocked").map(deal => (
                            <TableRow key={deal.id} className="bg-yellow-50 dark:bg-yellow-950/20">
                              <TableCell className="font-medium">
                                {deal.merchant_name || deal.merchant_id}
                              </TableCell>
                              <TableCell>{deal.gift_card_brand || "-"}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{deal.deal_type}</Badge>
                              </TableCell>
                              <TableCell className="max-w-[250px]">
                                <div className="space-y-1">
                                  {deal.blocked_reason_codes?.map((code, idx) => (
                                    <Badge key={idx} variant="destructive" className="text-xs mr-1">
                                      {code}
                                    </Badge>
                                  ))}
                                  {deal.blocked_reason_text && (
                                    <p className="text-xs text-muted-foreground">{deal.blocked_reason_text}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs">
                                {deal.discount_percent && <div>discount: {deal.discount_percent}%</div>}
                                {deal.qualifying_spend_cad && <div>spend: ${deal.qualifying_spend_cad}</div>}
                                {deal.reward_value && <div>reward: {deal.reward_value} {deal.reward_unit}</div>}
                                {deal.buy_amount && <div>buy: ${deal.buy_amount}</div>}
                              </TableCell>
                              <TableCell>
                                <Badge variant={deal.confidence >= 0.8 ? "default" : deal.confidence >= 0.6 ? "secondary" : "destructive"}>
                                  {Math.round(deal.confidence * 100)}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedDeal(deal);
                                    setShowDealDialog(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>

        {/* CR385: READ Diagnostics Drawer */}
        <ReadDiagnosticsDrawer
          open={showDiagDrawer}
          onOpenChange={setShowDiagDrawer}
          parsed={diagParsed}
          step={diagStep}
        />

        {/* Step Detail Dialog */}
        <Dialog open={showStepDialog} onOpenChange={setShowStepDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Step Details - {selectedStep?.step_phase}</DialogTitle>
            </DialogHeader>
            {selectedStep && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Phase</p>
                    <p className="font-medium">{selectedStep.step_phase}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <Badge className={STATUS_COLORS[selectedStep.status]}>
                      {selectedStep.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Merchant</p>
                    <p className="font-medium">{selectedStep.merchant_id || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Prompt Key</p>
                    <p className="font-mono text-xs">{selectedStep.prompt_key || "-"}</p>
                  </div>
                </div>
                
                {/* Text Acquisition Info (SCAN phase) */}
                {selectedStep.step_phase === "SCAN" && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded">
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Text Acquisition
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Source Type</p>
                        <Badge variant="outline">{selectedStep.text_source_type || "unknown"}</Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Character Count</p>
                        <p className="font-medium">{selectedStep.text_char_count?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                    {selectedStep.text_excerpt_used && (
                      <div className="mt-3">
                        <p className="text-muted-foreground text-xs mb-1">Text Excerpt (first 500 chars)</p>
                        <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-[100px] whitespace-pre-wrap">
                          {selectedStep.text_excerpt_used}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Candidate Text (EXTRACT/VALIDATE phases) */}
                {(selectedStep.step_phase === "EXTRACT" || selectedStep.step_phase === "VALIDATE") && selectedStep.input_candidate_text && (
                  <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded">
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Quote className="h-4 w-4" />
                      Input Candidate Text
                    </p>
                    <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-[100px] whitespace-pre-wrap">
                      "{selectedStep.input_candidate_text}"
                    </pre>
                    {selectedStep.input_context_text && (
                      <div className="mt-2">
                        <p className="text-muted-foreground text-xs mb-1">Context Text</p>
                        <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-[60px] whitespace-pre-wrap">
                          {selectedStep.input_context_text}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
                
                {selectedStep.error_message && (
                  <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
                    <p className="text-sm text-red-700 dark:text-red-300">{selectedStep.error_message}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* AI Log Dialog - CR059: Enhanced debugging view */}
        <Dialog open={showAiLogDialog} onOpenChange={setShowAiLogDialog}>
          <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>AI Call Log</DialogTitle>
              <DialogDescription>
                Raw input/output from the AI model call
              </DialogDescription>
            </DialogHeader>
            {selectedAiLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Model</p>
                    <p className="font-mono">{selectedAiLog.model_used}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Temperature</p>
                    <p>{selectedAiLog.temperature ?? "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Max Tokens</p>
                    <p>{selectedAiLog.max_output_tokens ?? "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Duration</p>
                    <p>{selectedAiLog.duration_ms ? `${selectedAiLog.duration_ms}ms` : "-"}</p>
                  </div>
                </div>
                
                {selectedAiLog.token_usage_json && (
                  <div className="p-3 bg-muted rounded">
                    <p className="text-sm font-medium mb-1">Token Usage</p>
                    <pre className="text-xs">{JSON.stringify(selectedAiLog.token_usage_json, null, 2)}</pre>
                  </div>
                )}
                
                {selectedAiLog.parse_error && (
                  <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Parse Error
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300 font-mono">{selectedAiLog.parse_error}</p>
                  </div>
                )}
                
                {/* CR059: Enhanced input payload view with text excerpt */}
                <div>
                  <p className="text-sm font-medium mb-2">Input Payload</p>
                  <ScrollArea className="h-[200px] border rounded p-2">
                    <pre className="text-xs whitespace-pre-wrap">
                      {JSON.stringify(selectedAiLog.input_payload_json, null, 2)}
                    </pre>
                  </ScrollArea>
                </div>

                {/* CR059: Show text excerpt if available in input */}
                {selectedAiLog.input_payload_json && typeof selectedAiLog.input_payload_json === 'object' && 'text_excerpt' in selectedAiLog.input_payload_json && (
                  <div>
                    <p className="text-sm font-medium mb-2">Page Text Excerpt (first 2000 chars)</p>
                    <ScrollArea className="h-[150px] border rounded p-2 bg-blue-50 dark:bg-blue-950">
                      <pre className="text-xs whitespace-pre-wrap">
                        {(selectedAiLog.input_payload_json as any).text_excerpt}
                      </pre>
                    </ScrollArea>
                  </div>
                )}
                
                {/* CR059: Always show raw response, highlighted if there's a parse error */}
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    Raw Response (Model Output)
                    {selectedAiLog.parse_error && (
                      <Badge variant="destructive" className="text-xs">Parse Failed - Check Below</Badge>
                    )}
                  </p>
                  <ScrollArea className={`h-[250px] border rounded p-2 ${selectedAiLog.parse_error ? 'bg-yellow-50 dark:bg-yellow-950 border-yellow-300' : ''}`}>
                    <pre className="text-xs whitespace-pre-wrap">
                      {selectedAiLog.raw_response_text || "(No raw response captured)"}
                    </pre>
                  </ScrollArea>
                </div>
                
                {selectedAiLog.parsed_response_json && (
                  <div>
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Parsed JSON (Successfully Extracted)
                    </p>
                    <ScrollArea className="h-[200px] border rounded p-2 bg-green-50 dark:bg-green-950">
                      <pre className="text-xs whitespace-pre-wrap">
                        {JSON.stringify(selectedAiLog.parsed_response_json, null, 2)}
                      </pre>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Deal Detail Dialog */}
        <Dialog open={showDealDialog} onOpenChange={setShowDealDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Deal Details - {selectedDeal?.gift_card_brand || selectedDeal?.merchant_name}</DialogTitle>
              <DialogDescription>
                Text-first pipeline: candidate → extracted → validated
              </DialogDescription>
            </DialogHeader>
            {selectedDeal && (
              <div className="space-y-4">
                {/* Candidate Text Section */}
                <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Quote className="h-4 w-4" />
                    Candidate Text (verbatim from source)
                  </p>
                  {selectedDeal.candidate_text ? (
                    <pre className="text-sm bg-muted p-3 rounded whitespace-pre-wrap">
                      "{selectedDeal.candidate_text}"
                    </pre>
                  ) : (
                    <p className="text-muted-foreground italic">No candidate text (legacy run)</p>
                  )}
                  {selectedDeal.context_text && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground mb-1">Context:</p>
                      <pre className="text-xs bg-muted p-2 rounded whitespace-pre-wrap">
                        {selectedDeal.context_text}
                      </pre>
                    </div>
                  )}
                  {selectedDeal.evidence_where_found && (
                    <div className="mt-2 flex gap-2">
                      <Badge variant="outline">Source: {selectedDeal.evidence_where_found}</Badge>
                      {selectedDeal.deal_type_guess && (
                        <Badge variant="outline">Guess: {selectedDeal.deal_type_guess}</Badge>
                      )}
                    </div>
                  )}
                </div>
                
                {/* CR058: Gate Status for Blocked Deals */}
                {selectedDeal.final_status === "blocked" && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-300 dark:border-yellow-800 rounded">
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      Blocked by Gate (Validation passed, but missing required values)
                    </p>
                    <div className="space-y-2">
                      {selectedDeal.blocked_reason_codes?.map((code, idx) => (
                        <Badge key={idx} variant="destructive" className="mr-1">
                          {code}
                        </Badge>
                      ))}
                      {selectedDeal.blocked_reason_text && (
                        <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-2">
                          {selectedDeal.blocked_reason_text}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Extracted Fields */}
                <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded">
                  <p className="text-sm font-medium mb-2">Extracted Fields</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Deal Type</p>
                      <Badge variant="outline">{selectedDeal.deal_type}</Badge>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Brand</p>
                      <p className="font-medium">{selectedDeal.gift_card_brand || "-"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Buy Amount</p>
                      <p className="font-medium">{selectedDeal.buy_amount ? `$${selectedDeal.buy_amount}` : "-"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Reward</p>
                      <p className="font-medium">{selectedDeal.reward_value ? `${selectedDeal.reward_value} ${selectedDeal.reward_unit}` : "-"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Discount %</p>
                      <p className="font-medium">{selectedDeal.discount_percent ? `${selectedDeal.discount_percent}%` : "-"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Qualifying Spend</p>
                      <p className="font-medium">{selectedDeal.qualifying_spend_cad ? `$${selectedDeal.qualifying_spend_cad}` : "-"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Confidence</p>
                      <Badge variant={selectedDeal.confidence >= 0.8 ? "default" : "secondary"}>
                        {Math.round(selectedDeal.confidence * 100)}%
                      </Badge>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Final Status</p>
                      <Badge variant={selectedDeal.final_status === "created" ? "default" : "destructive"}>
                        {selectedDeal.final_status || "created"}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* CR131: Validation Details with benefit_type + raw_benefit_text */}
                {(selectedDeal.validation_status || selectedDeal.benefit_type || selectedDeal.raw_benefit_text) && (
                  <div className={`p-4 rounded border ${
                    selectedDeal.validation_status === "valid" 
                      ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800"
                      : selectedDeal.validation_status === "invalid"
                        ? "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800"
                        : "bg-muted border-border"
                  }`}>
                    <p className="text-sm font-medium mb-3 flex items-center gap-2">
                      {selectedDeal.validation_status === "valid" ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : selectedDeal.validation_status === "invalid" ? (
                        <XCircle className="h-4 w-4 text-red-600" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                      )}
                      Validation: {selectedDeal.validation_status || "pending"}
                    </p>
                    
                    {/* CR131: Show raw_benefit_text + normalized benefit_type + percent */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3 p-3 bg-background/50 rounded border">
                      <div>
                        <p className="text-xs text-muted-foreground">Deal ID</p>
                        <p className="font-mono text-xs truncate" title={selectedDeal.id}>
                          {selectedDeal.id.substring(0, 8)}...
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Raw Benefit Text</p>
                        <p className="font-medium text-xs">
                          {selectedDeal.raw_benefit_text || selectedDeal.parsed_ai_json?.raw_benefit_text || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Benefit Type</p>
                        <Badge variant="outline" className="text-xs">
                          {selectedDeal.benefit_type || selectedDeal.parsed_ai_json?.benefit_type || "-"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Discount %</p>
                        <p className="font-medium">
                          {selectedDeal.discount_percent ? `${selectedDeal.discount_percent}%` : "-"}
                        </p>
                      </div>
                    </div>
                    
                    {/* CR131: Show decision + reason_code */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Decision</p>
                        <Badge variant={selectedDeal.validation_status === "valid" ? "default" : "destructive"}>
                          {selectedDeal.validation_status === "valid" ? "ACCEPT" : selectedDeal.validation_status === "invalid" ? "REJECT" : "PENDING"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Reason Code</p>
                        <Badge variant="outline" className="text-xs font-mono">
                          {selectedDeal.validation_reason_code || selectedDeal.parsed_ai_json?.reason_code || "-"}
                        </Badge>
                      </div>
                    </div>
                    
                    {selectedDeal.validation_reasons && Array.isArray(selectedDeal.validation_reasons) && selectedDeal.validation_reasons.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground mb-1">Validation Reasons:</p>
                        <ul className="text-sm list-disc list-inside">
                          {selectedDeal.validation_reasons.map((reason: string, idx: number) => (
                            <li key={idx}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedDeal.validation_needs_visual && (
                      <Badge variant="outline" className="mt-2">Needs Visual Validation</Badge>
                    )}
                  </div>
                )}
                
                {/* Raw AI Output */}
                {selectedDeal.raw_ai_response && (
                  <div>
                    <p className="text-sm font-medium mb-2">Raw AI Response</p>
                    <ScrollArea className="h-[150px] border rounded p-2">
                      <pre className="text-xs whitespace-pre-wrap">
                        {selectedDeal.raw_ai_response}
                      </pre>
                    </ScrollArea>
                  </div>
                )}
                
                {selectedDeal.ai_parse_error && (
                  <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">Parse Error</p>
                    <p className="text-sm text-red-700 dark:text-red-300">{selectedDeal.ai_parse_error}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
